import { classNames } from '@fullstory/util/fmt';
import React from 'react';
import { identifyComponent } from '../../util';
import { Flex } from '../Flex';
import { SelectProps } from '../Select';
import css from './SelectMenu.module.scss';
import { SelectMenuProps, useSelectMenu } from './useSelectMenu';

export type { OptionType } from './types';

interface SelectMenuComponentProps<T> extends SelectMenuProps<T> {
  /**
   * If true, a controlled TextInput will appear at the top of the list to filter the options
   * by their label.
   */
  filterable?: boolean;

  /**
   * If provided, sets max height on the options and hides the overflow
   */
  menuHeight?: 385 | 600;

  /**
   * If provided, sets max width for the select menu content
   */
  menuWidth?: SelectProps['contentWidth'];
}

export function SelectMenu<T>({
  filterable = false,
  menuWidth,
  menuHeight,
  testId,
  ...selectMenuProps
}: SelectMenuComponentProps<T>) {
  const { Select, selectProps, Filter, filterProps, filterRef, Options, optionsProps } =
    useSelectMenu({
      testId: testId ? testId : 'select-menu',
      ...selectMenuProps,
    });
  return (
    <Select
      data-component="SelectMenu"
      onFirstUpdate={() => filterRef.current?.focus()}
      contentWidth={menuWidth}
      contentClassName={classNames({ [css.minWidth]: !!selectMenuProps.clearable })}
      {...selectProps}
      {...identifyComponent('select-menu', selectProps)}
    >
      <Flex column alignItems="stretch" style={{ maxHeight: menuHeight }}>
        {filterable && <Filter ref={filterRef} {...filterProps} />}
        <Options {...optionsProps} />
      </Flex>
    </Select>
  );
}
